import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Package, Layers } from "lucide-react";
import { formatDOP, formatNum } from "@/lib/format";

/**
 * PresentationPicker — Selector emergente de presentación de venta.
 *
 * Muestra al usuario todas las unidades disponibles (unidad base + sub-unidades)
 * para un producto que tenga `sale_units` configuradas. Al elegir, dispara
 * `onPick(product, unit|null)` — unit=null → unidad base.
 *
 * Uso típico: envolver el botón/fila de un producto en la tabla del POS.
 */
export default function PresentationPicker({
  product,
  onPick,
  children,
  align = "start",
  side = "right",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const units = Array.isArray(product?.sale_units)
    ? product.sale_units.filter((u) => u && u.active !== false && u.unit && Number(u.ratio) > 0)
    : [];

  const baseStock = Number(product?.stock ?? 0);
  const basePrice = Number(product?.price ?? 0);
  const baseUnit = product?.unit || "UND";

  const handlePick = (unit) => {
    setOpen(false);
    onPick?.(product, unit || null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        className="p-0 w-72"
        data-testid={testid || `presentation-picker-${product?.sku}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-border bg-muted/40">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Elige presentación
            </div>
          </div>
          <div className="text-sm font-semibold mt-0.5 truncate" title={product?.name}>
            {product?.name}
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {/* Base unit */}
          <button
            type="button"
            onClick={() => handlePick(null)}
            disabled={baseStock <= 0}
            className={`w-full text-left px-3 py-2.5 flex items-center gap-2 border-b border-border/40 hover:bg-accent transition-colors ${
              baseStock <= 0 ? "opacity-40 cursor-not-allowed" : ""
            }`}
            data-testid={`pp-opt-base-${product?.sku}`}
          >
            <Package className="w-4 h-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{baseUnit}</div>
              <div className="text-[11px] text-muted-foreground">
                Stock: {formatNum(baseStock)} {baseUnit}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="data-cell font-bold text-primary text-sm">{formatDOP(basePrice)}</div>
              <div className="text-[10px] text-muted-foreground">unidad base</div>
            </div>
          </button>
          {/* Alternate sale units */}
          {units.map((u) => {
            const stock = Math.floor(baseStock * Number(u.ratio) * 100) / 100;
            const noStock = stock <= 0;
            return (
              <button
                key={u.unit}
                type="button"
                onClick={() => handlePick(u)}
                disabled={noStock}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-2 border-b border-border/40 last:border-b-0 hover:bg-accent transition-colors ${
                  noStock ? "opacity-40 cursor-not-allowed" : ""
                }`}
                data-testid={`pp-opt-${u.unit}-${product?.sku}`}
              >
                <span className="w-4 h-4 shrink-0 rounded bg-amber-500/20 border border-amber-500/40 text-amber-700 dark:text-amber-300 text-[10px] font-black flex items-center justify-center">
                  ½
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {u.unit}
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary uppercase">
                      Suelto
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Stock: {formatNum(stock)} {u.unit} · 1 {baseUnit} = {u.ratio} {u.unit}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="data-cell font-bold text-primary text-sm">
                    {formatDOP(Number(u.price))}
                  </div>
                  <div className="text-[10px] text-muted-foreground">por {u.unit}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="px-3 py-1.5 text-[10px] text-muted-foreground bg-muted/30 border-t border-border">
          Podrás cambiar la unidad después desde el carrito.
        </div>
      </PopoverContent>
    </Popover>
  );
}
